
import { defineComponent, onBeforeMount, reactive } from 'vue'
import moment from 'moment'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// Module Components
import DiscountActions from '@/modules/BillingModule/components/DiscountActions/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { BillingDiscountT, BillingDiscountPayloadT } from '@/core/types/billing.types'

// Realisations
import { billing } from '@/core/realisations/billing.realisation'

// Constants
import { BillingDiscountsColumns } from '@/core/constants/columns/BillingDiscounts.columns'
import { DISCOUNT_ENTITY_TYPES, DISCOUNT_UNIT_TYPES } from '@/core/constants/billing.constants'

// Composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'failed-fetch': FailedFetch,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        cheap: Cheap,
        //
        'discount-actions': DiscountActions,
    },
    setup() {
        const notifications = useNotifications()
        const state = reactive(new DataState<BillingDiscountT>({}))

        const loadDiscounts = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const response = await billing.getDiscounts(page)
                state.list = response.data
                state.pagination.total_pages = response.meta.last_page

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadDiscounts(1)
        })

        const { actions_state, startCreate } = useActions()

        const onDiscountCreate = async (payload: BillingDiscountPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await billing.createDiscount(payload)
                notifications.notify({ title: 'Скидка добавлена', type: 'success' })
                actions_state.is_actions_opened = false

                await loadDiscounts(state.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При добавлении скидки произошла ошибка',
                    error,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const normalizeDate = (date: string | null): string => {
            if (!date) return '-'
            return moment(date).format('DD.MM.YYYY')
        }

        const getExpiredStatus = (date: string | null): string => {
            if (!date) return 'ACTIVE'

            const from = moment()
            const to = moment(date)

            const difference = from.diff(to, 'days')

            if (difference >= 0) {
                return 'IN_ACTIVE'
            }

            return 'ACTIVE'
        }

        return {
            BillingDiscountsColumns,
            DISCOUNT_ENTITY_TYPES,
            DISCOUNT_UNIT_TYPES,
            state,
            loadDiscounts,
            actions_state,
            startCreate,
            onDiscountCreate,
            normalizeDate,
            getExpiredStatus,
        }
    },
})
